"use client"

import React, { useRef, useState, useEffect } from "react"
import { useTransitionRouter } from "next-view-transitions"
import Line from "@/components/line/line"
import gsap from "gsap"
import sanitizeHtml from "sanitize-html"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import Modal from "@/components/modal/Modal"
import { useRouter } from "next/navigation"
import { useScreenType } from "@/contexts/ScreenTypeContext"
import TextSplit from "../ui/TextSplit"

gsap.registerPlugin(ScrollTrigger)

export default function FeaturedWork({ projects }) {
  const router = useRouter()
  const transitionRouter = useTransitionRouter()
  const workListRef = useRef(null)
  const { isDesktop, isBigscreen } = useScreenType()
  const [modal, setModal] = useState({ active: false, index: 0 })

  /** CLICK HANDLER WITH VIEW TRANSITION */
  const handleClick = (e, slug) => {
    e.preventDefault()

    if (!document.startViewTransition) {
      router.push(`/work/${slug}`)
      return
    }

    document.startViewTransition(() => {
      transitionRouter.push(`/work/${slug}`)
    })
  }

  useEffect(() => {
    if (!workListRef.current) return

    const items = gsap.utils.toArray(".work-item")

    gsap.fromTo(
      items,
      { opacity: 0, x: -50, filter: "blur(20px)" },
      {
        opacity: 1,
        x: 0,
        filter: "blur(0px)",
        duration: 1,
        ease: "power3.out",
        stagger: 0.4,
        scrollTrigger: {
          trigger: workListRef.current,
          start: "top 80%",
          once: true
        }
      }
    )

    return () => {
      ScrollTrigger.getAll().forEach(t => t.kill())
      gsap.killTweensOf(items)
    }
  }, [])

  return (
    <section ref={workListRef} className="relative container py-24 lg:py-32 flex flex-col justify-center max-w-screen-2xl mx-auto">
      <h2 className="mb-12">Featured work</h2>

      <div className="relative">
        {projects.map((project, index) => {
          const media = project?._embedded?.["wp:featuredmedia"]?.[0]

          return (
            <a key={index} href={`/work/${project.slug}`} onClick={e => handleClick(e, project.slug)}>
              <div className="hover:cursor-pointer hover:text-orange-600 transition-all duration-200 ease-linear hover:scale-105" onMouseEnter={() => setModal({ active: true, index })} onMouseLeave={() => setModal({ active: false, index })}>
                <div className="grid grid-cols-12 gap-8 lg:gap-16 max-w-full my-8 items-center work-item">
                  <figure className="relative rounded-xl lg:rounded-3xl overflow-hidden col-span-3 aspect-square">
                    <img src={media?.source_url} alt={media?.alt_text || ""} className="object-cover rounded-lg md:rounded-xl xl:rounded-3xl h-full w-full" style={{ viewTransitionName: `work-${project.slug}` }} />
                  </figure>

                  <div className="col-span-9">
                    <div className="flex flex-col lg:grid lg:grid-cols-2 lg:gap-12 items-start lg:items-center">
                      <h2
                        className="text-3xl md:text-4xl lg:text-[3rem] !leading-[1.2]"
                        dangerouslySetInnerHTML={{
                          __html: sanitizeHtml(project.title.rendered)
                        }}
                      />

                      <TextSplit animateOnScroll={true} delay={0.45}>
                        <div
                          className="text-base md:text-base lg:text-lg"
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHtml(project.excerpt.rendered, {
                              ALLOWED_TAGS: []
                            })
                          }}
                          style={{ margin: 0 }}
                        />
                      </TextSplit>
                    </div>
                  </div>
                </div>

                {index + 1 < projects.length && <Line />}
              </div>
            </a>
          )
        })}

        {(isDesktop || isBigscreen) && <Modal projects={projects} modal={modal} />}
      </div>
    </section>
  )
}
